import { type ChangeEvent, useEffect, useRef, useState } from "react";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import { Chip, IconButton, Stack, type SxProps, type Theme, Tooltip, Typography } from "@mui/material";

import { CHIP_TEXT_MAX_WIDTH, CHIP_WIDTH } from "./consts";
import { EllipsisText } from "./EllipsisText";
import { useComponentWidth } from "./hooks";

type Props = {
  disabled?: boolean;
  maxFiles?: number;
  onFilesChange?: (files: File[]) => void;
  files: File[];
  sx?: SxProps<Theme>;
};

export const FileAttachment = ({ disabled = false, maxFiles = 2, onFilesChange, files, sx }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [visibleChipsCount, setVisibleChipsCount] = useState<number>(0);

  const { ref, width } = useComponentWidth();

  const hiddenFilesCount = files.length - visibleChipsCount;
  const showMoreIndicator = hiddenFilesCount > 0;

  useEffect(() => {
    const chipsToShow = Math.floor(width / (CHIP_WIDTH + (showMoreIndicator ? 42 : 0)));
    setVisibleChipsCount(chipsToShow);
  }, [files, width, showMoreIndicator]);

  const handleAttachment = () => {
    if (files.length >= maxFiles) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles) {
      const remainingSlots = maxFiles - files.length;
      const newFiles = Array.from(selectedFiles)
        .slice(0, remainingSlots)
        .filter((newFile) => !files.some((existingFile) => existingFile.name === newFile.name));

      const updatedFiles = [...files, ...newFiles];
      onFilesChange?.(updatedFiles);
    }

    event.target.value = "";
  };

  const handleFileRemove = (fileToRemove: File) => {
    const updatedFiles = files.filter((file) => file !== fileToRemove);
    onFilesChange?.(updatedFiles);
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        data-cy="file-input"
        multiple={files.length < maxFiles}
      />
      <IconButton
        size="small"
        onClick={handleAttachment}
        disabled={disabled || files.length >= maxFiles}
        sx={{
          position: "absolute",
          bottom: 8,
          left: 8,
          color: files.length >= maxFiles ? "text.disabled" : "action.active",
          zIndex: 1,
          ...sx,
        }}
      >
        <AttachFileIcon fontSize="small" />
      </IconButton>
      <Stack
        direction="row"
        spacing={2}
        ref={ref}
        sx={{
          position: "absolute",
          bottom: 10,
          left: 48,
          width: "calc(100% - 100px)", // Leave space for the ask button
          flexWrap: "nowrap",
          overflow: "hidden",
        }}
      >
        {files.slice(0, visibleChipsCount).map((file, index) => (
          <Tooltip key={`${file.name}-${index}`} title={file.name} enterDelay={500}>
            <Chip
              label={<EllipsisText maxWidth={CHIP_TEXT_MAX_WIDTH}>{file.name}</EllipsisText>}
              onDelete={() => {
                handleFileRemove(file);
              }}
              deleteIcon={<CloseIcon />}
              size="small"
              data-cy="selected-file-chip"
            />
          </Tooltip>
        ))}
        {showMoreIndicator && (
          <Tooltip title={`${hiddenFilesCount} more file${hiddenFilesCount > 1 ? "s" : ""}`}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, alignSelf: "center" }}>
              +{hiddenFilesCount} files
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </>
  );
};
